import {
  Check,
  ChevronRight,
  Languages,
  LogOut,
  Moon,
  Sun,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTheme, useTranslation } from '@doccareplus/shared/lib';
import type { SupportedLanguage } from '@doccareplus/shared/lib';
import { apiClientFactory } from '@doccareplus/api-client';
import { resolveMobileNav } from '@doccareplus/kernel';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import {
  applySwitchedContext,
  logout,
  selectActiveContext,
  selectMemberships,
  selectUser,
} from '@doccareplus/kernel/application/store/slices/auth.slice';
import { toastApiError } from '@doccareplus/kernel/application';
import { ConfirmDialog } from '@doccareplus/ui';
import { useAppDispatch, useAppSelector } from '@doccareplus/kernel/application/store/hooks';
import { MobileSheetFrame, MobileRoleSwitcherSheet } from './MobileShell';
import { useClinicAccessQuery } from '@features/clinic-access';
import { MOBILE_BOTTOM_TAB_MIDDLE_CAPACITY, mobileNavPresentation } from '../navModel';

// Each language is labelled in its own script, the standard convention for a
// language picker so users can recognise their language without reading English.
const languageOptions: { code: SupportedLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'hin', label: 'Hinglish' },
];

export function MobileMorePage() {
  const { t, i18n } = useTranslation('common');
  const { resolvedTheme, toggleTheme } = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const activeContext = useAppSelector(selectActiveContext);
  const memberships = useAppSelector(selectMemberships);
  const accessQuery = useClinicAccessQuery();
  const [roleSheetOpen, setRoleSheetOpen] = useState(false);
  const [languageSheetOpen, setLanguageSheetOpen] = useState(false);
  const [signOutConfirming, setSignOutConfirming] = useState(false);
  const isDark = resolvedTheme === 'dark';
  const activeLanguage = languageOptions.find((option) => option.code === i18n.language) ?? languageOptions[0];
  const activeMembership = memberships.find((membership) => membership.clinicId === activeContext?.clinicId) ?? memberships[0];
  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : t('workspace.userFallback');
  const role = activeContext?.role ?? activeMembership?.role ?? '';
  const initials = user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : 'U';
  const roleOptions = activeMembership?.roles?.length ? activeMembership.roles : activeMembership ? [activeMembership.role] : [];
  // A membership always has at least one assigned role, so the switcher must always show one. If the active
  // role no longer matches an assigned role (e.g. a stale/persisted value), fall back to the first assigned role.
  const matchedRole = roleOptions.find((r) => r.toLowerCase() === role.toLowerCase()) ?? roleOptions[0] ?? '';
  // Mirrors MobileShell's bottom-nav split: front-desk/owner roles already have the calendar view as
  // their primary "Schedule"/"Requests" tab, so this list only adds it for roles that don't. Whatever
  // isn't promoted into MobileShell's 3 middle bottom-tab slots (in the same priority order, filtered
  // by the same permissions) spills into this list — the two surfaces can never drift apart.
  const isFrontDesk = matchedRole.toLowerCase() === 'receptionist' || matchedRole.toLowerCase() === 'owner';
  const { moreListPaths } = resolveMobileNav(isFrontDesk, accessQuery.data, MOBILE_BOTTOM_TAB_MIDDLE_CAPACITY);
  const visibleMoreRoutes = moreListPaths.map((path) => ({ path, ...mobileNavPresentation[path] }));

  const handleRoleChange = async (nextRole: string) => {
    if (!activeMembership) return;

    try {
      const result = await authApi.switchContext(activeMembership.clinicId, nextRole);
      apiClientFactory.addHeader('X-Clinic-Id', result.clinicId);
      apiClientFactory.addHeader('X-Active-Role', result.role);
      dispatch(applySwitchedContext(result));
      localStorage.setItem('doccareplus.current_clinic', result.clinicId);
      localStorage.setItem('doccareplus.current_role', result.role);
      setRoleSheetOpen(false);
    } catch (error) {
      toastApiError(error, t('errors.switchRoleFailed'));
    }
  };

  const handleLanguageChange = (code: SupportedLanguage) => {
    void i18n.changeLanguage(code);
    setLanguageSheetOpen(false);
  };

  return (
    <section className="flex flex-col gap-4 py-2">
      <div className="px-0.5">
        <h1 className="text-[24px] font-bold text-[var(--text)]">{t('workspace.nav.more')}</h1>
        <p className="mt-1 text-[13px] leading-relaxed text-[var(--text-2)]">{activeMembership?.clinicName ?? t('workspace.clinicName')}</p>
      </div>

      <div className="flex items-center gap-3 rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-1)]">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--primary-weak)] text-[15px] font-bold text-[var(--primary)]">
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[16px] font-bold text-[var(--text)]">{displayName}</span>
          <span className="block truncate text-[12.5px] font-medium text-[var(--text-3)]">
            {t(`role.${role.toLowerCase()}`, { defaultValue: role })} · {activeMembership?.clinicName ?? t('workspace.clinicName')}
          </span>
        </span>
        <button
          type="button"
          aria-label={t('workspace.mobile.switchViewAs')}
          onClick={() => setRoleSheetOpen(true)}
          className="rounded-[9px] border-none bg-[var(--primary-weak)] px-3 py-2 text-[12.5px] font-bold text-[var(--primary)]"
        >
          {t('workspace.mobile.switchRole')}
        </button>
      </div>

      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-1 shadow-[var(--shadow-1)]">
        {visibleMoreRoutes.map((route, index) => {
          const Icon = route.icon;
          const hasDivider = index < visibleMoreRoutes.length - 1;

          return (
            <NavLink
              key={route.path}
              to={route.path}
              className={`flex items-center gap-3 rounded-[12px] px-3 py-3 text-[14px] font-bold text-[var(--text)] no-underline hover:no-underline ${hasDivider ? 'border-b border-[var(--border)]' : ''}`}
            >
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[var(--surface-2)] text-[var(--text-2)]">
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1 truncate">{t(route.labelKey)}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-3)]" aria-hidden="true" />
            </NavLink>
          );
        })}
      </div>

      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-1 shadow-[var(--shadow-1)]">
        <button
          type="button"
          onClick={() => setLanguageSheetOpen(true)}
          className="flex w-full items-center gap-3 rounded-[12px] border-b border-[var(--border)] px-3 py-3 text-left text-[14px] font-bold text-[var(--text)]"
        >
          <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[var(--surface-2)] text-[var(--text-2)]">
            <Languages className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1 truncate">{t('workspace.mobile.language')}</span>
          <span className="shrink-0 text-[13px] font-semibold text-[var(--text-3)]">{activeLanguage.label}</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-3)]" aria-hidden="true" />
        </button>
        <div className="flex w-full items-center gap-3 rounded-[12px] border-b border-[var(--border)] px-3 py-3 text-left text-[14px] font-bold text-[var(--text)]">
          <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[var(--surface-2)] text-[var(--text-2)]">
            {isDark ? (
              <Moon className="h-[18px] w-[18px]" aria-hidden="true" />
            ) : (
              <Sun className="h-[18px] w-[18px]" aria-hidden="true" />
            )}
          </span>
          <span className="min-w-0 flex-1 truncate">{t('workspace.mobile.darkTheme')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={isDark}
            aria-label={t('workspace.mobile.darkTheme')}
            onClick={toggleTheme}
            className={`relative inline-flex h-[26px] w-[46px] shrink-0 items-center rounded-full border border-transparent transition-colors ${isDark ? 'bg-[var(--primary)]' : 'bg-[var(--surface-3,var(--border))]'
              }`}
          >
            <span
              className={`inline-block h-[20px] w-[20px] transform rounded-full bg-white shadow-[var(--shadow-1)] transition-transform ${isDark ? 'translate-x-[22px]' : 'translate-x-[3px]'
                }`}
            />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setSignOutConfirming(true)}
          className="flex w-full items-center gap-3 rounded-[12px] px-3 py-3 text-left text-[14px] font-bold text-[var(--danger)]"
        >
          <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[var(--danger-bg)] text-[var(--danger)]">
            <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1 truncate">{t('actions.signOut')}</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--danger)]" aria-hidden="true" />
        </button>
      </div>

      <div className="px-1 text-center text-[11.5px] font-medium text-[var(--text-3)]">
        {t('workspace.mobile.version')}
      </div>

      {roleSheetOpen && (
        <MobileRoleSwitcherSheet
          roleOptions={roleOptions}
          activeRole={matchedRole}
          userInitials={initials}
          onRoleChange={handleRoleChange}
          onClose={() => setRoleSheetOpen(false)}
        />
      )}

      {languageSheetOpen && (
        <MobileSheetFrame title={t('workspace.mobile.chooseLanguage')} onClose={() => setLanguageSheetOpen(false)}>
          <div className="flex flex-col gap-[10px]">
            {languageOptions.map((option) => {
              const active = option.code === activeLanguage.code;

              return (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => handleLanguageChange(option.code)}
                  className={`flex min-h-[64px] items-center justify-between gap-4 rounded-[16px] border px-[18px] py-[14px] text-left text-[15px] font-bold ${active
                      ? 'border-[var(--primary)] bg-[var(--primary-weak)] text-[var(--primary)]'
                      : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text)]'
                    }`}
                >
                  <span className="truncate">{option.label}</span>
                  {active && <Check className="h-5 w-5 shrink-0" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </MobileSheetFrame>
      )}

      {signOutConfirming && (
        <ConfirmDialog
          title={t('actions.signOutConfirmTitle')}
          description={t('actions.signOutConfirmDescription')}
          confirmLabel={t('actions.signOut')}
          cancelLabel={t('actions.staySignedIn')}
          onConfirm={() => dispatch(logout())}
          onCancel={() => setSignOutConfirming(false)}
        />
      )}
    </section>
  );
}
